// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 — asp-pot public/announcements.js (the signed-in half): pending announcements shown one
// at a time as a modal — gradient header "📣 What's new · 1 of N", 22px title, "Posted <when> by
// <who>", markdown-lite body (## heading, - bullet with emoji icon, **bold**), and the "I've read
// this" button locked for a countdown ("I've read this (N)" / "Please read before continuing —
// the button unlocks in N seconds."), then #0078D4 with a pulse (spec §0.5).
//
// OwlRx data: in-app notifications (GET /notifications?unread=true). A notification whose
// event_type names an announcement (potApi.isAnnouncementEvent) is the pop-up; "I've read this"
// = POST /notifications/mark-read for that id, so the acknowledgement is per user, server-side.
// GAP(D-POT-2): no announcement entity (audience, expiry, per-post ack delay, "show again to
// all") — the delay is the ASP default of 10s; posting one is a platform action.
import { useEffect, useState } from 'react'
import * as api from '../../../api/client'
import { formatUsDateTime } from '../../../api/format'
import { useAuth } from '../../../auth/AuthContext'
import { POT } from '../potTheme'
import { isAnnouncementEvent, parseAnnouncementBody, splitBold, POT_ANNOUNCEMENT_ACK_DELAY_SEC } from '../potApi'

function Inline({ text }: { text: string }) {
  return <>{splitBold(text).map((p, i) => p.bold ? <strong key={i}>{p.text}</strong> : <span key={i}>{p.text}</span>)}</>
}

export function AnnouncementBody({ body }: { body: string | null | undefined }) {
  return (
    <>
      {parseAnnouncementBody(body).map((b, i) => {
        if (b.kind === 'heading') return <div key={i} style={{ fontSize: 12, fontWeight: 700, color: POT.blue, textTransform: 'uppercase', letterSpacing: '.06em', margin: '14px 0 8px' }}><Inline text={b.text} /></div>
        if (b.kind === 'bullet') return (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', marginBottom: 6, background: '#F5F9FF', border: '1px solid #DCE9F7', borderRadius: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1.2, flexShrink: 0 }} aria-hidden>{b.icon}</span><span><Inline text={b.text} /></span>
          </div>
        )
        return <p key={i} style={{ margin: '0 0 12px' }}>{b.lines.map((l, j) => <span key={j}>{j > 0 && <br />}<Inline text={l} /></span>)}</p>
      })}
    </>
  )
}

export default function Announcements() {
  const { me } = useAuth()
  const [queue, setQueue] = useState<api.NotificationItem[]>([])
  const [left, setLeft] = useState(POT_ANNOUNCEMENT_ACK_DELAY_SEC)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!me?.authenticated) return
    let alive = true
    api.getNotifications(true, 50).then(r => {
      if (!alive) return
      setQueue(r.items.filter(n => !n.is_read && isAnnouncementEvent(n.event_type)))
    }).catch(() => { /* offline: try next page load */ })
    return () => { alive = false }
  }, [me?.authenticated])

  const current = queue[0]
  useEffect(() => {
    if (!current) return
    setLeft(POT_ANNOUNCEMENT_ACK_DELAY_SEC)
    const t = setInterval(() => setLeft(n => (n > 0 ? n - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [current])

  if (!current) return null
  const unlocked = left <= 0
  const ack = async () => {
    if (!unlocked || saving) return
    setSaving(true)
    try { await api.markNotificationsRead({ ids: [current.id] }) } catch { /* will show again next time */ }
    setSaving(false)
    setQueue(q => q.slice(1))
  }
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="pot-ann-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(11,42,74,.72)', backdropFilter: 'blur(3px)', zIndex: 10050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <style>{'@keyframes potAnnIn{from{opacity:0;transform:translateY(18px) scale(.98)}to{opacity:1;transform:none}}@keyframes potAnnPulse{0%,100%{box-shadow:0 0 0 0 rgba(0,120,212,.45)}50%{box-shadow:0 0 0 10px rgba(0,120,212,0)}}'}</style>
      <div style={{ background: '#fff', borderRadius: 18, maxWidth: 640, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,.35)', fontFamily: POT.font, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'potAnnIn .35s ease-out' }}>
        <div style={{ padding: '22px 26px 18px', background: POT.headerGradient, color: '#fff' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', opacity: .85, marginBottom: 6 }}>📣 What's new{queue.length > 1 ? ` · 1 of ${queue.length}` : ''}</div>
          <h3 id="pot-ann-title" style={{ fontSize: 22, fontWeight: 700, margin: 0, lineHeight: 1.25 }}>{current.title}</h3>
          <div style={{ fontSize: 12, opacity: .8, marginTop: 8 }}>Posted {formatUsDateTime(current.created_at)}</div>
        </div>
        <div style={{ padding: '18px 26px', fontSize: 14, lineHeight: 1.6, color: POT.text, overflow: 'auto' }}>
          <AnnouncementBody body={current.body} />
          {current.link && <p style={{ margin: '8px 0 0' }}><a href={current.link} style={{ color: POT.blue }}>Open</a></p>}
        </div>
        <div style={{ padding: '14px 26px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderTop: `1px solid ${POT.border}`, background: POT.bg }}>
          <span style={{ fontSize: 12, color: POT.text2 }}>{unlocked ? "Clicking confirms you've read this notice." : `Please read before continuing — the button unlocks in ${left} second${left === 1 ? '' : 's'}.`}</span>
          <button type="button" disabled={!unlocked || saving} onClick={() => { void ack() }}
            style={{ padding: '12px 22px', border: 'none', borderRadius: 10, background: unlocked ? POT.blue : POT.borderDk, color: '#fff', fontWeight: 700, fontSize: 14, cursor: unlocked ? 'pointer' : 'not-allowed', minWidth: 190, transition: 'background .3s', animation: unlocked ? 'potAnnPulse 1.4s ease-out 3' : undefined, fontFamily: POT.font }}>
            {saving ? 'Saving…' : unlocked ? "I've read this" : `I've read this (${left})`}
          </button>
        </div>
      </div>
    </div>
  )
}
